import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Button, Icon } from '@repo/ui';
import { Alert, Close, LoaderGradient } from '@repo/ui/icons';
import { memo } from 'react';
import type { CheckoutAttemptTrackingContext, ReviewDialogParams } from '@/lib/analytics/client/trackers/checkout-funnel';
import type { SimulationStatus } from '@/services/simulate/simulatedPosition.helpers';
import VaultV1Recap from '@/components/Bundler/ConfirmDialog/Recaps/VaultV1Recap';
import { ErrorServiceUnavailable } from '@/components/common/Error/ErrorServiceUnavailable';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { useWeb3Context } from '@/contexts/Web3Context';
import useCloseDialogOnWalletChange from '@/hooks/useCloseDialogOnWalletChange';
import { useDeferredSubmit } from '@/hooks/useDeferredSubmit';
import { useReviewFunnelTracking } from '@/lib/analytics/client/hooks/useReviewFunnelTracking';
interface VaultV1OperationReviewDialogProps {
  type: 'deposit' | 'withdraw';
  amount: bigint;
  initialPosition: bigint;
  finalPosition: bigint | undefined;
  vaultAddress: Address;
  isButtonDisabled: boolean;
  /** First preparation, nothing to show yet: skeleton recap + loading CTA. */
  isLoading: boolean;
  /**
   * A block-poll refresh (rebuild / requirements re-key / re-sim) is in flight behind values
   * already on screen. The recap and CTA stay as they are; a click lands once it settles.
   */
  isRefreshing?: boolean;
  handleContinue: (tracking: CheckoutAttemptTrackingContext) => void;
  reviewTrackingParams: ReviewDialogParams;
  /** Outcome of the review-time asset-changes simulation. */
  simulationStatus: SimulationStatus;
  /** A simulation ran but couldn't verify the position-after, so it's an estimate. */
  isUnverifiedFinalPosition: boolean;
  /**
   * Reason the action can't be prepared (tx/requirements/vault-data error or a
   * stale transaction). When set, the disabled Continue button is paired with a
   * `role="alert"` explanation instead of being silently inert.
   */
  errorReason?: string;
}
const VaultV1OperationReviewDialog = t0 => {
  const $ = _c(44);
  const {
    type,
    amount,
    initialPosition,
    finalPosition,
    vaultAddress,
    isButtonDisabled,
    isLoading,
    isRefreshing: t1,
    handleContinue,
    reviewTrackingParams,
    simulationStatus,
    isUnverifiedFinalPosition,
    errorReason
  } = t0;
  const isRefreshing = t1 === undefined ? false : t1;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    account,
    switchToTargetChain
  } = useWeb3Context();
  useCloseDialogOnWalletChange();
  const {
    trackClose,
    confirmAttempt
  } = useReviewFunnelTracking(reviewTrackingParams, !isLoading);
  const isSimulationLoading = simulationStatus === "loading";
  const isSimulationBlacklisted = simulationStatus === "blacklist";
  const canSubmit = !isButtonDisabled && !isLoading && !isSimulationLoading && !isSimulationBlacklisted;
  const t2 = isButtonDisabled || isSimulationBlacklisted;
  let t3;
  if ($[0] !== confirmAttempt || $[1] !== handleContinue) {
    t3 = () => handleContinue(confirmAttempt());
    $[0] = confirmAttempt;
    $[1] = handleContinue;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== canSubmit || $[4] !== isRefreshing || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      isRefreshing,
      canSubmit,
      shouldCancel: t2,
      submit: t3
    };
    $[3] = canSubmit;
    $[4] = isRefreshing;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    request: requestContinue,
    isPending: isContinuePending
  } = useDeferredSubmit(t4);
  const isButtonLoading = isLoading || isSimulationLoading || isContinuePending;
  const isDisabled = !canSubmit || isContinuePending;
  let t5;
  if ($[8] !== toggleDialog || $[9] !== trackClose) {
    t5 = () => {
      trackClose();
      toggleDialog(false);
    };
    $[8] = toggleDialog;
    $[9] = trackClose;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleClose = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="text-title-sm">Review</span>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Close />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== handleClose) {
    t8 = <div className="flex items-center justify-between gap-[16px]">{t6}<Button size="icon" aria-label="Close" onClick={handleClose} variant="secondary">{t7}</Button></div>;
    $[13] = handleClose;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ErrorServiceUnavailable minHeight="364px" />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== amount || $[17] !== finalPosition || $[18] !== initialPosition || $[19] !== isSimulationLoading || $[20] !== isUnverifiedFinalPosition || $[21] !== type || $[22] !== vaultAddress) {
    t10 = <SectionErrorBoundary fallback={t9}><VaultV1Recap type={type} amount={amount} initialPosition={initialPosition} finalPosition={finalPosition} vaultAddress={vaultAddress} isFinalPositionLoading={isSimulationLoading} isSimulationFallback={isUnverifiedFinalPosition} /></SectionErrorBoundary>;
    $[16] = amount;
    $[17] = finalPosition;
    $[18] = initialPosition;
    $[19] = isSimulationLoading;
    $[20] = isUnverifiedFinalPosition;
    $[21] = type;
    $[22] = vaultAddress;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== isSimulationBlacklisted) {
    t11 = isSimulationBlacklisted && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="vault-v1-blacklist-error"><Icon className="text-destructive" icon={<Alert />} /><span className="text-destructive text-xs">This transaction can't be completed due to a simulation error.</span></div>;
    $[24] = isSimulationBlacklisted;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== errorReason || $[27] !== isButtonLoading || $[28] !== isSimulationBlacklisted) {
    t12 = !isSimulationBlacklisted && !isButtonLoading && errorReason && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="vault-v1-preparation-error"><Icon className="text-destructive" icon={<Alert />} /><span className="text-destructive text-xs">{errorReason}</span></div>;
    $[26] = errorReason;
    $[27] = isButtonLoading;
    $[28] = isSimulationBlacklisted;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== account.isWrongChain || $[31] !== isButtonLoading || $[32] !== isDisabled || $[33] !== isRefreshing || $[34] !== requestContinue || $[35] !== switchToTargetChain || $[36] !== type) {
    t13 = account.isWrongChain ? <Button size="lg" onClick={switchToTargetChain}>Switch chain</Button> : <Button data-testid="confirm-button" data-refreshing={isRefreshing} onClick={() => requestContinue()} size="lg" disabled={isDisabled}>{isButtonLoading ? <LoaderGradient /> : null}{isButtonLoading ? "Loading" : type === "deposit" ? "Deposit" : "Withdraw"}</Button>;
    $[30] = account.isWrongChain;
    $[31] = isButtonLoading;
    $[32] = isDisabled;
    $[33] = isRefreshing;
    $[34] = requestContinue;
    $[35] = switchToTargetChain;
    $[36] = type;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t8) {
    t14 = <div className="gap-md flex flex-col" data-testid="confirm-dialog">{t8}{t10}{t11}{t12}{t13}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t8;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
};
export default memo(VaultV1OperationReviewDialog);